import React from 'react';
import { useState, useRef, useEffect } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  RefreshCw,
  ChevronLeft, // <-- Added for "Previous"
  ChevronRight, // <-- Added for "Next"
} from 'lucide-react';

// Define a type for a video object in a playlist
interface Video {
  url: string;
  // You can add other properties like title, id, etc.
  // For example, from your other code snippet: sop_instance_uid?: string;
  [key: string]: any; // Allow other properties
}

interface VideoPlayerProps {
  // The source can now be a single URL string or an array of Video objects
  source: string | Video[];
  isDarkMode: boolean;
  showRecordedVideo: any; // Kept this prop as it was in your original code
}

const VideoPlayer = ({
  source,
  isDarkMode,
  showRecordedVideo,
}: VideoPlayerProps) => {
  // --- STATE MANAGEMENT ---
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(false);

  // --- NEW PLAYLIST STATE ---
  const [currentIndex, setCurrentIndex] = useState(0);

  // --- DERIVED VALUES ---
  // Check if the source is a playlist (an array)
  const isPlaylist = Array.isArray(source);
  // Get the videos array if it's a playlist, otherwise an empty array
  const videos = isPlaylist ? source : [];
  // Determine the current video source URL
  const currentSrc = isPlaylist ? videos[currentIndex]?.url : source;

  // --- EFFECTS ---

  // Effect to handle video events (timeupdate, durationchange, ended)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime);
      if (video.duration > 0) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };

    const handleDurationChange = () => {
      setDuration(video.duration);
    };


    const handleEnded = () => {
      setIsPlaying(false);
      // ** PLAYLIST LOGIC: Auto-advance to the next video **
      if (isPlaylist && currentIndex < videos.length - 1) {
        setCurrentIndex((prevIndex) => prevIndex + 1);
      }
    };

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('ended', handleEnded);
    };
  }, [currentSrc, isPlaylist, currentIndex, videos.length]); // Dependencies updated

  // Effect to reset the player when the source changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setProgress(0);
    setDuration(0);

    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      // Auto-play the new video in the playlist
      if (isPlaylist) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
        }
      }
    }
  }, [currentSrc, isPlaylist]);

  // Effect to reset the index if the entire source array changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [source]);

  // Effect to hide controls after a delay
  useEffect(() => {
    if (showControls) {
      const timer = setTimeout(() => {
        setShowControls(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showControls]);


  // --- HANDLER FUNCTIONS ---

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current || !duration) return;
    const newTime = (parseFloat(e.target.value) / 100) * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(parseFloat(e.target.value));
  };

  const resetVideo = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = 0;
    if (isPlaying) {
      videoRef.current.play();
    }
  };

  const enterFullscreen = () => {
    if (videoRef.current?.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || time === 0) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || e.target === videoRef.current) {
      setShowControls(!showControls);
    }
  };

  // --- NEW PLAYLIST HANDLERS ---
  const playNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPlaylist && currentIndex < videos.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const playPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPlaylist && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  
  const handleSelectVideo = (index: number) => {
    setCurrentIndex(index);
  };

  // --- RENDER ---

  // Don't render if there's no valid source
  if (!currentSrc) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-black rounded-lg text-white">
        No video source provided.
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 h-[40rem] lg:h-[36rem]">
      {/* Main Video Display */}
      <div
        className="relative overflow-hidden h-full rounded-lg group flex-grow"
        onClick={handleContainerClick}
      >
        <video
          // ** IMPORTANT: Using a key forces React to re-mount the video element
          // when the src changes, which is crucial for reliable behavior. **
          key={currentSrc}
          ref={videoRef}
          src={currentSrc}
          crossOrigin="anonymous"
          className="h-full w-full object-contain bg-black"
          onClick={(e) => {
            e.stopPropagation();
            togglePlay();
          }}
        />

        {/* Custom Controls Overlay */}
        <div
          className={`absolute bottom-0 left-0 right-0 p-2 transition-opacity ${
            showControls ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          } ${isDarkMode ? 'bg-black bg-opacity-60' : 'bg-white bg-opacity-60'}
          ${showRecordedVideo ? 'opacity-100' : ''}
          `}
        >
          <div className="relative w-full h-2 bg-gray-300 dark:bg-gray-700 rounded-full cursor-pointer">
            <div
              className="absolute top-0 left-0 h-full bg-blue-600 dark:bg-blue-500 rounded-full"
              style={{ width: `${progress}%` }}
            />
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={handleProgressChange}
              className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <div className="flex items-center gap-2">
              <button onClick={(e) => {e.stopPropagation(); togglePlay();}} className="p-1.5 rounded-full text-white bg-black/20 hover:bg-black/40">
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
              
              {/* ** NEW: Previous/Next buttons for playlist ** */}
              {isPlaylist && (
                <>
                  <button onClick={playPrev} disabled={currentIndex === 0} className="p-1.5 rounded-full text-white bg-black/20 hover:bg-black/40 disabled:opacity-50 disabled:cursor-not-allowed">
                    <ChevronLeft size={16} />
                  </button>
                  <button onClick={playNext} disabled={currentIndex >= videos.length - 1} className="p-1.5 rounded-full text-white bg-black/20 hover:bg-black/40 disabled:opacity-50 disabled:cursor-not-allowed">
                    <ChevronRight size={16} />
                  </button>
                </>
              )}

              <button onClick={(e) => {e.stopPropagation(); toggleMute();}} className="p-1.5 rounded-full text-white bg-black/20 hover:bg-black/40">
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <button onClick={(e) => {e.stopPropagation(); resetVideo();}} className="p-1.5 rounded-full text-white bg-black/20 hover:bg-black/40">
                <RefreshCw size={16} />
              </button>
              <span className="text-xs font-medium text-white">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
            <button onClick={(e) => {e.stopPropagation(); enterFullscreen();}} className="p-1.5 rounded-full text-white bg-black/20 hover:bg-black/40">
              <Maximize size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Playlist Sidebar (only shown if it's a playlist with more than one video) */}
      {isPlaylist && videos.length > 1 && (
        <div className="flex-shrink-0 w-full md:w-52 h-48 md:h-full overflow-y-auto space-y-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <h3 className="font-semibold text-sm p-2 text-gray-700 dark:text-gray-300">
            Playlist ({currentIndex + 1} / {videos.length})
          </h3>
          {videos.map((video, index) => (
            <button
              key={video.url + index} // Use a more unique key if available, like video.sop_instance_uid
              onClick={() => handleSelectVideo(index)}
              className={`w-full text-left p-2 rounded-md transition-colors text-sm font-medium ${
                index === currentIndex
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'
              }`}
            >
              Video {index + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;